import { useState } from "react";
import { toast } from "sonner";
import { Facebook } from "lucide-react";
import { BUSINESS } from "@/data/content";
import { api } from "@/lib/api";
import { scrollToId } from "@/lib/scroll";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.newsletter({ email, source: "footer" }); toast.success("You're in! Watch for exclusive offers."); setEmail(""); }
    catch { toast.error("Couldn't subscribe. Try again."); } finally { setLoading(false); }
  };

  return (
    <footer className="bg-charcoal text-cream pt-24 pb-10 grain relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-8 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 pb-16 border-b border-cream/10">
          <div>
            <h3 className="font-serif-display text-4xl md:text-5xl leading-tight">Get first taste of<br /><em className="text-terracotta not-italic">specials & offers</em>.</h3>
            <p className="text-cream/60 mt-4 max-w-md">Join the list for seasonal menu drops, catering deals, and a treat on your birthday.</p>
          </div>
          <form onSubmit={subscribe} data-testid="newsletter-form" className="flex flex-col justify-center gap-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <input required type="email" data-testid="newsletter-email" placeholder="you@email.com" value={email} onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-cream/5 border border-cream/20 focus:border-terracotta outline-none rounded-full px-6 py-4 placeholder-cream/40 transition-colors" />
              <button type="submit" disabled={loading} data-testid="newsletter-submit" className="bg-terracotta text-cream px-8 py-4 rounded-full text-sm hover:bg-cream hover:text-ink transition-colors duration-300 active:scale-95 disabled:opacity-60">
                {loading ? "..." : "Subscribe"}
              </button>
            </div>
            <p className="text-cream/40 text-xs">No spam. Unsubscribe anytime.</p>
          </form>
        </div>

        <div className="grid md:grid-cols-3 gap-10 py-14 text-sm">
          <div>
            <p className="text-terracotta text-xs uppercase tracking-[0.2em] mb-4">Visit</p>
            <p className="text-cream/70">{BUSINESS.address}</p>
            <a href={BUSINESS.phoneHref} className="text-cream/70 hover:text-terracotta transition-colors block mt-2">{BUSINESS.phone}</a>
          </div>
          <div>
            <p className="text-terracotta text-xs uppercase tracking-[0.2em] mb-4">Hours</p>
            {BUSINESS.hours.map((h) => <p key={h.day} className="text-cream/70">{h.day} · {h.time}</p>)}
          </div>
          <div>
            <p className="text-terracotta text-xs uppercase tracking-[0.2em] mb-4">Explore</p>
            <div className="flex flex-col gap-2">
              {["menu", "catering", "reviews", "contact"].map((id) => (
                <button key={id} onClick={() => scrollToId(id)} className="text-cream/70 hover:text-terracotta transition-colors text-left capitalize">{id}</button>
              ))}
              <a href={BUSINESS.social.facebook} target="_blank" rel="noreferrer" data-testid="footer-facebook" className="inline-flex items-center gap-2 text-cream/70 hover:text-terracotta transition-colors mt-1"><Facebook size={15} /> Follow us on Facebook</a>
            </div>
          </div>
        </div>

        <div className="pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-cream/40 text-xs">© {new Date().getFullYear()} Sero's Restaurant. All rights reserved.</p>
        </div>
        <p className="font-serif-display text-[22vw] md:text-[15vw] leading-[0.8] text-cream/[0.04] text-center mt-6 select-none pointer-events-none">SERO'S</p>
      </div>
    </footer>
  );
}
